import { EmailSearchSchema, VerifyEmailSchema } from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	createFileRoute,
	Link,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";
import { AuthPage } from "@/components/auth/auth-page";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import {
	apiClient,
	authVerifyEmailCreate,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/verify-email/")({
	component: VerifyEmail,
	head: () =>
		seo({
			title: "Verify Email",
			description: "Verify your email address",
			keywords: "verify, email, otp, verification",
		}),
	validateSearch: (search: Record<string, unknown>) => {
		const normalizedSearch = {
			...search,
			email:
				typeof search.email === "string" && search.email.trim() === ""
					? undefined
					: search.email,
		};
		const result = EmailSearchSchema.safeParse(normalizedSearch);
		if (result.success) {
			return result.data;
		}
		return { email: undefined };
	},
});

export function VerifyEmail() {
	const router = useRouter();
	const search = useSearch({
		strict: false,
	});

	const form = useForm<z.infer<typeof VerifyEmailSchema>>({
		resolver: zodResolver(VerifyEmailSchema),
		defaultValues: {
			email: search.email ?? "",
			token: "",
		},
	});

	const onSubmit = async (data: z.infer<typeof VerifyEmailSchema>) => {
		try {
			await requestWithHandling<void>(() =>
				authVerifyEmailCreate(data.token, data.email),
			);
			toast.success("Email verified");
			router.navigate({ to: "/auth/login" });
		} catch (error) {
			toast.error("Verification failed", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const handleResendCode = async () => {
		const email = form.getValues("email");
		if (!email) {
			toast.error("Email address is required");
			return;
		}

		try {
			await requestWithHandling<void>(() =>
				apiClient.api.authRequestEmailVerificationCreate({
					email,
				}),
			);
			toast.success("Verification code resent", {
				description: "Please check your email for the new code.",
			});
		} catch (error) {
			toast.error("Failed to resend code", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<AuthPage
			title="Verify your email"
			description="Enter the code we sent you, or request a new one."
		>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Email</FormLabel>
								<FormControl>
									<Input
										{...field}
										type="email"
										required
										autoComplete="email"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="token"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Verification code</FormLabel>
								<FormControl>
									<InputOTP maxLength={6} {...field}>
										<InputOTPGroup>
											<InputOTPSlot index={0} />
											<InputOTPSlot index={1} />
											<InputOTPSlot index={2} />
										</InputOTPGroup>
										<InputOTPSeparator />
										<InputOTPGroup>
											<InputOTPSlot index={3} />
											<InputOTPSlot index={4} />
											<InputOTPSlot index={5} />
										</InputOTPGroup>
									</InputOTP>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
						<Button
							type="button"
							variant="outline"
							className="h-11 flex-1"
							onClick={handleResendCode}
						>
							Resend code
						</Button>
						<Button type="submit" className="h-11 flex-1 cursor-pointer">
							Verify email
						</Button>
					</div>
				</form>
			</Form>

			<p className="mt-8 text-center text-base text-muted-foreground">
				<Link
					to="/auth/login"
					className="font-medium text-foreground underline-offset-4 hover:underline"
				>
					Back to sign in
				</Link>
			</p>
		</AuthPage>
	);
}
